import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { CheckIcon, ExclamationTriangleIcon, InformationCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { loadingState } from "../../redux/reducers/loadingReducer";
import { classNames } from '../../helpers';


/* ---------- shared look (new FitClub theme, same as FcConfirmDialog) ---------- */

const TONE = {
     success: { box: "bg-[#E8F7EE] text-[#1F6B3A]", Icon: CheckIcon },
     error: { box: "bg-[#FFE8EB] text-[#B4232F]", Icon: ExclamationTriangleIcon },
     warning: { box: "bg-[#FFF4E5] text-[#8A4B00]", Icon: ExclamationTriangleIcon },
     info: { box: "bg-[#F3E9FF] text-indigo-bz", Icon: InformationCircleIcon },
};

function ToneIcon({ type }) {
     const t = TONE[type];
     if (!t) return null;
     return (
          <span className={classNames("flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl", t.box)}>
               <t.Icon aria-hidden="true" className="size-6" />
          </span>
     );
}

const primaryBtn = "flex min-w-28 items-center justify-center gap-2 rounded-2xl bg-brown-coffee px-5 py-3 text-sm font-extrabold text-white hover:bg-light-brown-coffee disabled:opacity-60";
const secondaryBtn = "flex min-w-28 items-center justify-center rounded-2xl border border-gray-200 bg-white px-5 py-3 text-sm font-extrabold text-gray-900 hover:bg-gray-50 disabled:opacity-60";
const backdropCls = "fixed inset-0 bg-[#1d1320]/45 transition-opacity duration-200 data-[closed]:opacity-0";
const panelCls = "w-full max-w-md rounded-t-3xl bg-white p-6 text-left shadow-2xl transition duration-200 data-[closed]:translate-y-4 data-[closed]:opacity-0 sm:rounded-3xl";

const Spinner = () => <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />;

/**
 * Message pop-up that replaces the old toasts (ToastProvider addToast / addSuccessToast / addErrorToast).
 * message = { type: success|error|warning|info|default, title?, text }. Success closes by itself after a few seconds.
 */
export function TwMessageDialog({ message, onClose }) {
     const open = !!message;
     const type = message?.type && TONE[message.type] ? message.type : "info";
     const title = message?.title || (type === "success" ? "Done" : type === "error" ? "Something went wrong" : type === "warning" ? "Please check" : "Good to know");
     React.useEffect(() => {
          if (!open || type !== "success") return undefined;
          const t = setTimeout(onClose, 3500);
          return () => clearTimeout(t);
          // eslint-disable-next-line react-hooks/exhaustive-deps
     }, [message]);
     return (
          <Dialog open={open} onClose={onClose} className="relative z-[70]">
               <DialogBackdrop transition className={backdropCls} />
               <div className="fixed inset-0 flex items-end justify-center p-0 sm:items-center sm:p-4">
                    <DialogPanel transition className={panelCls}>
                         <div className="flex items-start gap-4">
                              <ToneIcon type={type} />
                              <div className="min-w-0 pt-0.5">
                                   <DialogTitle className="text-lg font-black text-gray-900">{title}</DialogTitle>
                                   {message?.text && <p className="mt-1 whitespace-pre-line break-words text-sm text-gray-600">{String(message.text)}</p>}
                              </div>
                         </div>
                         <div className="mt-6 flex justify-end">
                              <button type="button" autoFocus onClick={onClose} className={classNames(primaryBtn, "w-full sm:w-auto")}>
                                   OK
                              </button>
                         </div>
                    </DialogPanel>
               </div>
          </Dialog>
     );
}

/** addNotificationDialog (ToastProvider): one message with one button. Same options as before. */
export function TwNotificationDialog({ notificationDialogContent, closeNotificationDialog = () => { }, setNotificationDialogContent, updateNotificationDialog, dialogBackdropClick = () => { closeNotificationDialog() } }) {
     const loadingReduxState = useSelector(loadingState);
     const c = notificationDialogContent;
     return (
          <Dialog open={!!c.dialogOpen} onClose={() => {
               if (c.isAllowBackdropClick && c.isAllowBackdropClick == true) {
                    dialogBackdropClick()
               }
          }} className="relative z-[65]">
               <DialogBackdrop transition className={backdropCls} />
               <div className="fixed inset-0 flex items-end justify-center p-0 sm:items-center sm:p-4">
                    <DialogPanel transition className={panelCls}>
                         <div className="flex items-start gap-4">
                              <ToneIcon type={c.dialogType} />
                              <div className="min-w-0 pt-0.5">
                                   {c.dialogTitle && <DialogTitle className="text-lg font-black text-gray-900">{c.dialogTitle}</DialogTitle>}
                                   {c.dialogMessage && <div className={classNames("text-sm text-gray-600", c.dialogTitle ? "mt-1" : "")}>{c.dialogMessage}</div>}
                              </div>
                         </div>
                         {c.isDialogBtnShow && (
                              <div className="mt-6 flex justify-end">
                                   <button
                                        type="button"
                                        className={classNames(primaryBtn, "w-full sm:w-auto")}
                                        disabled={loadingReduxState.actionLoading}
                                        onClick={async () => {
                                             if (c.dialogBtnClick) {
                                                  let returnDialogBtnClick = await c.dialogBtnClick({ notificationDialogContent: c, setNotificationDialogContent, updateNotificationDialog })
                                                  if (returnDialogBtnClick == undefined || returnDialogBtnClick != false) {
                                                       closeNotificationDialog()
                                                  }
                                             } else {
                                                  closeNotificationDialog()
                                             }
                                        }}
                                   >
                                        {loadingReduxState.actionLoading && <Spinner />}
                                        {c.dialogBtnTitle || "OK"}
                                   </button>
                              </div>
                         )}
                    </DialogPanel>
               </div>
          </Dialog>
     );
}

/** Yes / No confirmation (addConfirmDialog, DeleteDialog, MarkAsPaidDialog, WarningDialog, …). Same props as before. */
export function TwConfirmDialog(props) {
     const { children, className = "", dialogType = "", dialogOpen, id = 1, dialogTitle = "WARNING", dialogMessage = "", btnCloseText = "No", isCloseBtn = true, onClose = () => { }, btnConfirmText = "Yes", isConfirmBtn = true, onConfirm = () => { } } = props

     const [dialogConfirmOpen, setConfirmDialogOpen] = useState(false)

     useEffect(() => {
          setConfirmDialogOpen(props.dialogOpen)
          return () => { }
          // eslint-disable-next-line react-hooks/exhaustive-deps
     }, [dialogOpen])

     const danger = dialogType === "error";
     return (
          <Dialog key={id} open={!!dialogConfirmOpen} onClose={setConfirmDialogOpen} className="relative z-[60]">
               <DialogBackdrop transition className={backdropCls} />
               <div className="fixed inset-0 flex items-end justify-center p-0 sm:items-center sm:p-4">
                    <DialogPanel transition className={classNames(panelCls, "relative")}>
                         <button
                              type="button"
                              aria-label="Close"
                              onClick={() => {
                                   onClose()
                                   setConfirmDialogOpen(false)
                              }}
                              className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                         >
                              <XMarkIcon aria-hidden="true" className="size-5" />
                         </button>
                         <div className="flex items-start gap-4 pr-8">
                              <ToneIcon type={dialogType} />
                              <div className="min-w-0 pt-0.5">
                                   {dialogTitle && <DialogTitle className="text-lg font-black text-gray-900">{dialogTitle}</DialogTitle>}
                                   {dialogMessage && <div className={classNames("text-sm text-gray-600", dialogTitle ? "mt-1" : "")}>{dialogMessage}</div>}
                                   {children}
                              </div>
                         </div>
                         <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                              {isCloseBtn && (
                                   <button
                                        type="button"
                                        onClick={() => {
                                             onClose()
                                             setConfirmDialogOpen(false)
                                        }}
                                        className={secondaryBtn}
                                   >
                                        {btnCloseText}
                                   </button>
                              )}
                              {isConfirmBtn && (
                                   <button
                                        type="button"
                                        onClick={() => {
                                             onConfirm()
                                             setConfirmDialogOpen(false)
                                        }}
                                        className={classNames(primaryBtn, danger ? "bg-[#B4232F] hover:bg-[#961C27]" : "")}
                                   >
                                        {btnConfirmText}
                                   </button>
                              )}
                         </div>
                    </DialogPanel>
               </div>
          </Dialog>
     )
}

export function TwDialog(props) {
     const { children, className = "", dialogOpen, id = 1, dialogTitle = "", dialogMessage = "", isFullContent = false, isAllowBackdropClick = true, showXBtn = true, btnChildren, onClose = (dialogConfirmOpen = false) => { } } = props

     const [dialogConfirmOpen, setConfirmDialogOpen] = useState(false)
     const zIndex = `z-[${1001 + id}]`


     useEffect(() => {
          setConfirmDialogOpen(props.dialogOpen)
          return () => { }
     }, [dialogOpen])

     return (
          <Dialog key={id} open={dialogConfirmOpen} onClose={() => {
               if (isAllowBackdropClick == true) {
                    setConfirmDialogOpen(false)
               }
          }} className={`relative z-50 ${zIndex}`}>
               <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-[#1d1320]/45 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
               />

               <div className={`fixed inset-0 z-50 ${zIndex} w-screen overflow-y-auto`}>
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                         <DialogPanel
                              transition
                              className="relative transform overflow-hidden rounded-3xl bg-white px-4 pb-4 pt-5 text-left shadow-2xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
                         >
                              {showXBtn && (<div className="absolute right-0 top-0 pr-4 pt-4 sm:block">
                                   <button
                                        type="button"
                                        onClick={() => {
                                             onClose(dialogConfirmOpen)
                                             setConfirmDialogOpen(false)
                                        }}
                                        className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                   >
                                        <span className="sr-only">Close</span>
                                        <XMarkIcon aria-hidden="true" className="size-6" />
                                   </button>
                              </div>)}
                              <div className="sm:flex sm:items-start">
                                   <div className={classNames("mt-3 text-center sm:mx-4 sm:mt-0 sm:text-left", isFullContent ? "w-full" : "")}>
                                        {dialogTitle && (<DialogTitle as="h3" className="text-xl font-bold text-gray-900">
                                             {dialogTitle}
                                        </DialogTitle>)}
                                        <div className="mt-2">
                                             {dialogMessage && (<p className="text-sm text-gray-500">
                                                  {dialogMessage}
                                             </p>)}
                                             {children}
                                        </div>
                                   </div>
                              </div>
                              {btnChildren && (<div className="mt-5 sm:mt-4 flex gap-3 flex-col sm:flex-row-reverse">
                                   {btnChildren}
                              </div>)}
                         </DialogPanel>
                    </div>
               </div>
          </Dialog>
     )
}
